import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Clock, AlertCircle } from "lucide-react";

export function FormAnalytics() {
  const [formStats, setFormStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFormAnalytics();
  }, []);

  const loadFormAnalytics = async () => {
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile?.org_id) return;

      // Get form templates with submission counts
      const { data: templates } = await supabase
        .from("form_templates")
        .select(`
          *,
          submissions:form_submissions(count, status)
        `)
        .eq("org_id", profile.org_id);

      if (templates) {
        const stats = templates.map((template: any) => {
          const submissions = template.submissions || [];
          const total = submissions.length;
          const completed = submissions.filter((s: any) => s.status === "completed").length;
          const draft = submissions.filter((s: any) => s.status === "draft").length;
          const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

          return {
            id: template.id,
            name: template.name,
            category: template.category,
            total,
            completed,
            draft,
            completionRate,
          };
        });

        setFormStats(stats);
      }
    } catch (error: any) {
      console.error("Error loading form analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading form analytics...</div>;
  }

  if (formStats.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No form data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Form Performance</h3>

      <div className="grid gap-4">
        {formStats.map((stat) => (
          <Card key={stat.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{stat.name}</CardTitle>
                  <CardDescription className="mt-1">
                    <Badge variant="outline">{stat.category}</Badge>
                  </CardDescription>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{stat.completionRate}%</div>
                  <p className="text-xs text-muted-foreground">Completion Rate</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <div>
                    <div className="text-2xl font-bold">{stat.completed}</div>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-500" />
                  <div>
                    <div className="text-2xl font-bold">{stat.draft}</div>
                    <p className="text-xs text-muted-foreground">In Progress</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-2xl font-bold">{stat.total}</div>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
